"use client";

import { datadogRum } from "@datadog/browser-rum";
import type { FC } from "react";
import { useEffect, useRef } from "react";
import { useUserContext } from "./user-context";

const DatadogCore: FC<{
  service: string;
  treatments?: Record<string, string>;
  userData?: { email?: string; userId?: string };
}> = ({ service, treatments, userData }) => {
  const version = process.env.NEXT_PUBLIC_COMMIT_SHA || "development";
  const lastVersion = useRef(version);
  const userContext = useUserContext(userData);

  useEffect(() => {
    try {
      console.log("[Datadog] Version:", version);

      // Only initialize if not already initialized or if version changed
      if (!datadogRum.getInternalContext() || lastVersion.current !== version) {
        lastVersion.current = version;
        datadogRum.init({
          applicationId: "85dc5232-2f23-40db-96b4-e2ff39c2df6f",
          clientToken: "pub3a41b898b58be888681bd1763723c3a6",
          site: "us5.datadoghq.com",
          service,
          env: process.env.NODE_ENV || "development",
          version,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 10,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: "allow",
          enableExperimentalFeatures: ["feature_flags"],
          allowedTracingUrls: ["https://*.yardzen.com", "http://localhost*"],
          beforeSend: (event: any) => {
            if (event.error) {
              // Filter out error that might be caused by link validation / pre-fetch rendering from email clicks
              // https://github.com/DataDog/browser-sdk/issues/2715
              const regex =
                /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;
              if (regex.test(event.error.message)) {
                return false;
              }
            }
            return true;
          },
        });

        datadogRum.startSessionReplayRecording();
        console.log(
          "[Datadog] RUM initialized successfully with version:",
          version,
        );
      }

      if (treatments) {
        console.log("[Datadog] Setting feature flag evaluations:", treatments);
        Object.entries(treatments).forEach(([feature, treatment]) => {
          datadogRum.addFeatureFlagEvaluation(feature, treatment);
        });
      }
    } catch (error) {
      console.error("[Datadog] Failed to initialize:", error);
    }
  }, [service, treatments, version]);

  // Bind user context to RUM session when user context changes
  useEffect(() => {
    if (!datadogRum.getInternalContext()) return;

    try {
      const context: Record<string, string | undefined> = {};

      // Add user context properties
      if (userContext.email) {
        context.email = userContext.email;
      }
      if (userContext.userId) {
        context.userId = userContext.userId;
      }
      if (userContext.anonymousId) {
        context.anonymousId = userContext.anonymousId;
      }
      if (userContext.ipAddress) {
        context.ipAddress = userContext.ipAddress;
      }
      if (userContext.country) {
        context.country = userContext.country;
      }

      // Set global context for all RUM events
      if (Object.keys(context).length > 0) {
        datadogRum.setGlobalContext(context);
        console.log("[Datadog] User context set:", context);
      }
    } catch (error) {
      console.error("[Datadog] Failed to set user context:", error);
    }
  }, [userContext]);

  return null;
};

export const Datadog = DatadogCore;
